<template>
  <b-nav-item-dropdown
    class="dropdown-notification mr-25"
    menu-class="dropdown-menu-media"
    right
  >
    <template #button-content>
      <feather-icon
        :badge="unreadNotifications.length"
        badge-classes="bg-danger"
        class="text-body"
        icon="BellIcon"
        size="21"
      />
    </template>

    <!-- Header -->
    <li class="dropdown-menu-header">
      <div class="dropdown-header d-flex">
        <h4 class="notification-title mb-0 mr-auto">
          Notifications
        </h4>
        <b-badge
          v-if="unreadNotifications.length > 0"
          pill
          variant="light-primary"
        >
          {{ unreadNotifications.length }} New
        </b-badge>
      </div>
    </li>

    <!-- Notifications -->
    <vue-perfect-scrollbar
      :settings="perfectScrollbarSettings"
      class="scrollable-container media-list scroll-area"
      tagname="li"
    >
      <p v-if="!unreadNotifications.length" class="media d-flex align-items-center">
        😕 No notifications available
      </p>

      <!-- Account Notification -->
      <div v-if="unreadNotifications.length > 0">
        
        <b-dropdown-item
          v-for="notification in unreadNotifications"
          :key="notification._id"
          @click="onNotificationItemClicked(notification)"
        >
          <div class="d-flex align-items-center justify-content-between m-0 p-0">
            <div>
              <p class="media-heading text-ellipsis p-0 m-0">
                <span class="font-weight-bolder inline-block w-100">
                  {{ notification.subject }}
                </span>
              </p>

              <p class="notification-text inline-block text-ellipsis w-100">
                {{ notification.message }}
              </p>
            </div>
          </div>
        </b-dropdown-item>
      </div>
    </vue-perfect-scrollbar>
  </b-nav-item-dropdown>
</template>

<script>
/**
 * DISCLAIMER: THIS IS USED ACROSS ALL PLATFORMS.
 */
import store from '@/store'
import Ripple from 'vue-ripple-directive'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

import { get } from "lodash";
import { computed, ref } from '@vue/composition-api'
import { BNavItemDropdown, BDropdownItem, BBadge, BMedia, BLink, BAvatar, BButton, BFormCheckbox, VBTooltip } from 'bootstrap-vue'

const SHORT_LOGO = require("@/assets/images/logo/logo-short.png")

export default {
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  components: {
    BNavItemDropdown,
    BDropdownItem,
    BBadge,
    BMedia,
    BLink,
    BAvatar,
    VuePerfectScrollbar,
    BButton,
    BFormCheckbox
  },
  computed: {
    unreadNotifications(){
      return this.$store.getters[`notifications/unreadNotifications`]
    },
  },
  beforeDestroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId)
    }
  },
  setup() {
    const perfectScrollbarSettings = {
      maxScrollbarLength: 60,
      wheelPropagation: false,
    }

    const navTagStore = computed({
      get: () => store.state.verticalMenu.tags,
    })

    const intervalId = ref()

    return {
      perfectScrollbarSettings,
      navTagStore,
      intervalId,
    }
  },
  // Watcher to play new notification sound whenever there is a new notification
  created() {
    // this.fetchUnreadNotifications()
    // this.seekNotificationPermissionIfNotAlreadyGranted()
    // this.intervalId = setInterval(this.fetchNewUnreadNotifications, 1000 * 10)
  },
  methods: {
    async onNotificationItemClicked(notification) {
      const lab_route = "admin-lab-details"
      const pharmacy_route = "admin-pharmacy-details"

      let route = { name: "admin-notifications", query: { notification_id: notification._id } }

      try {
        await this.useJwt().mainService.notifications.markAsRead(notification._id);
        this.$store.commit(`notifications/MARK_AS_READ`, notification._id);
      } catch (error) {
        console.log("There was an error marking notification as read", error)
      }

      if (this.isMain) {
        const medical_consultation_route = "admin-medical-consultation-edit"
        if (notification.resources.consultation_id) {
          route = { name: medical_consultation_route, params: { id: notification.resources.consultation_id } }
        } else if (notification.resources.lab_id) {
          route = { name: lab_route, params: { id: notification.resources.lab_id } }
        } else if (notification.resources.pharmacy_id) {
          route = { name: pharmacy_route, params: { id: notification.resources.lab_id } }
        }
        return this.$router.push(route)
      }

      const consultation_route = "admin-consultation-edit" // CAVEAT: This is the same for lab and pharmacy platforms
      if (this.isPharmacy && notification.resources.pharmacy_consultation_id) {
        route = { name: consultation_route, params: { id: notification.resources.pharmacy_consultation_id } }
      } else if (this.isLab && notification.resources.lab_consultation_id) {
        route = { name: consultation_route, params: { id: notification.resources.lab_consultation_id } }
      }

      this.$router.push(route)
    },
    async fetchUnreadNotifications() {
      try {
        const response = await this.useJwt().mainService.notifications.list({ limit: 10, read: "no" });
        const notifications = get(response, 'data.data', {});
        this.$store.commit(`notifications/SET_NOTIFICATIONS`, notifications);
      } catch (error) {
        const error_message =
          get(error, "response.data.message") || error.message;
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error!",
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: error_message,
          },
        });
      }
    },
    async fetchNewUnreadNotifications() {
      try {
        const lastNotification = this.unreadNotifications[0]
        const query = { limit: 10, read: "no" }
        if (lastNotification) {
          query.cursor = lastNotification._id
        }

        const response = await this.useJwt().mainService.notifications.list(query);
        const newNotifications = get(response, 'data.data', {});
        if (newNotifications.length > 0) {
          this.$store.commit(`notifications/ADD_NOTIFICATIONS`, newNotifications);
          this.showBrowserNotification()
        }
      } catch (error) {
        const error_message =
          get(error, "response.data.message") || error.message;
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error!",
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: error_message,
          },
        });
      }
    },
    showBrowserNotification() {
      if (!Notification || !("Notification" in window)) {
        console.log("This browser does not support notifications.");
        // NOT showing an alert because notifications are only available in supported browsers.
        return;
      }
      if (Notification.permission === "granted") {
        const notification = new Notification("New Notification", {
          body: "You have a new notification",
          icon: SHORT_LOGO,
          lang: "en",
          dir: "ltr",
        })
        // notification.
        this.playAudio() // this will fail to play until user interacts with the browser
        console.log("NOTIFICATION", notification)
      }
    }
  }
}
</script>

<style scoped>
  .text-ellipsis {
    width: 260px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
</style>
